import { Assignment as AssignmentIcon, CalendarToday as CalendarIcon, Close as CloseIcon, Flag as FlagIcon, TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, ListItemText, Typography } from "@mui/material";
import React from "react";
import { CoachPlan } from "../types/coachPlan";
import { DateUtils } from "../utils/dateUtils";

interface CoachPlanModalProps {
  open: boolean;
  onClose: () => void;
  plan: CoachPlan | null;
  coachName?: string;
}

export const CoachPlanModal: React.FC<CoachPlanModalProps> = ({ open, onClose, plan, coachName = "Coach" }) => {
  if (!plan) {
    return null;
  }

  // Convert dates from Firestore timestamps or strings to Date objects
  const createdAt = DateUtils.toDate(plan.createdAt);
  const updatedAt = plan.updatedAt ? DateUtils.toDate(plan.updatedAt) : null;

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return DateUtils.formatForDisplay(date);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "primary";
      case "approved":
        return "info";
      case "draft":
        return "warning";
      default:
        return "default";
    }
  };

  const renderGoals = () => {
    if (!plan.goals || Object.keys(plan.goals).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No goals defined
        </Typography>
      );
    }

    return Object.entries(plan.goals).map(([key, value]) => (
      <Box key={key} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {key.charAt(0).toUpperCase() + key.slice(1)}
        </Typography>
        {typeof value === "object" && value !== null ? (
          <Box sx={{ ml: 2 }}>
            {Object.entries(value).map(([subKey, subValue]) => (
              <Typography key={subKey} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{subKey}:</strong> {String(subValue)}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ ml: 2 }}>
            {String(value)}
          </Typography>
        )}
      </Box>
    ));
  };

  const renderActionItems = () => {
    if (!plan.actionItems || plan.actionItems.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No action items defined
        </Typography>
      );
    }

    return (
      <List dense>
        {plan.actionItems.map((item, index) => (
          <ListItem key={index} sx={{ px: 0 }}>
            <ListItemText
              primary={typeof item === "string" ? item : item.title || item.description || `Action ${index + 1}`}
              secondary={typeof item === "object" && item.description && item.title ? item.description : undefined}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderMetrics = () => {
    if (!plan.metrics || Object.keys(plan.metrics).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No metrics defined
        </Typography>
      );
    }

    return Object.entries(plan.metrics).map(([key, value]) => (
      <Box key={key} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {key.charAt(0).toUpperCase() + key.slice(1)}
        </Typography>
        {typeof value === "object" && value !== null ? (
          <Box sx={{ ml: 2 }}>
            {Object.entries(value).map(([subKey, subValue]) => (
              <Typography key={subKey} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{subKey}:</strong> {String(subValue)}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ ml: 2 }}>
            {String(value)}
          </Typography>
        )}
      </Box>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              {coachName} Plan
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Chip label={plan.planType.charAt(0).toUpperCase() + plan.planType.slice(1)} size="small" color="primary" variant="outlined" />
              {plan.status && <Chip label={plan.status.charAt(0).toUpperCase() + plan.status.slice(1)} size="small" color={getStatusColor(plan.status)} />}
            </Box>
          </Box>
          <Button onClick={onClose} size="small">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(createdAt)}
            </Typography>
            {updatedAt && createdAt && updatedAt.getTime() !== createdAt.getTime() && (
              <>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated: {formatDate(updatedAt)}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Goals Section */}
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FlagIcon color="primary" />
                <Typography variant="h6">Goals</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {renderGoals()}
            </CardContent>
          </Card>

          {/* Action Items Section */}
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AssignmentIcon color="primary" />
                <Typography variant="h6">Action Items</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {renderActionItems()}
            </CardContent>
          </Card>

          {/* Metrics Section */}
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6">Metrics</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {renderMetrics()}
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
